/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import { SocialShareTemplate } from "../../../Molecules/SocialShareTemplate";
import { SocialShare } from "@quintype/components";
import { SectionTag } from "../../../Atoms/SectionTag";
import { StoryHeadline } from "../../../Atoms/StoryHeadline";
import { Subheadline } from "../../../Atoms/Subheadline";
import AsideCollection from "../../AsideCollection";
import { AuthorCard } from "../../../Atoms/AuthorCard";
import { CaptionAttribution } from "../../../Atoms/CaptionAttribution";
import { HeroImage } from "../../../Atoms/HeroImage";
import { PublishDetails } from "../../../Atoms/PublishDetail";
import { StoryTags } from "../../../Atoms/StoryTags";
import { StoryElementCard } from "../../../Molecules/StoryElementCard";
import "./text-story.m.css";

export const StoryTemplate = ({ story = {}, config = {}, children, storyElementsConfig, widgetComp, adComponent }) => {
  const {
    theme = "",
    asideCollection = {},
    templateType = "default",
    noOfVisibleCards = 0,
    publishedDetails = {},
    authorDetails = {},
    verticalShare = "",
    shareIconType = "plain-color-svg"
  } = config;

  const visibledCards = noOfVisibleCards < 0 ? story.cards : story.cards.slice(0, noOfVisibleCards);

  const HeaderCard = () => {
    return (
      <div styleName="header-details">
        <SectionTag story={story} />
        <StoryHeadline story={story} />
        <Subheadline story={story} showFullContent />
      </div>
    );
  };

  const VerticalShare = () => {
    return (
      <SocialShare
        template={SocialShareTemplate}
        fullUrl={story.url}
        title={story.headline}
        vertical={true}
        theme={theme}
        iconType={shareIconType}
      />
    );
  };

  const AsideCollectionCard = () => {
    return (
      asideCollection && (
        <div styleName="aside-collection tablet-index-2">
          <AsideCollection horizontal={true} {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
        </div>
      )
    );
  };

  const StoryData = () => {
    return (
      <div styleName="gap-16">
        {authorDetails && <AuthorCard story={story} template={authorDetails.template} opts={authorDetails.opts} />}
        <div styleName="timestamp-social-share">
          <PublishDetails story={story} opts={publishedDetails} />
          {!verticalShare && (
            <SocialShare
              template={SocialShareTemplate}
              fullUrl={story.url}
              title={story.headline}
              theme={theme}
              iconType={shareIconType}
            />
          )}
        </div>
        {visibledCards.map(card => {
          return (
            <StoryElementCard
              story={story}
              card={card}
              key={card.id}
              config={storyElementsConfig}
              adComponent={adComponent}
              widgetComp={widgetComp}
            />
          );
        })}
        <div styleName="story-tags">
          <StoryTags tags={story.tags} />
        </div>
        {children}
      </div>
    );
  };

  const SideColumn = () => {
    return (
      asideCollection && (
        <div styleName="text-story-side-column">
          <AsideCollection {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
        </div>
      )
    );
  };

  const heroPriorityCenterTemplate = () => {
    return (
      <>
        <div styleName="hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="story-content-inner-wrapper">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <AsideCollectionCard />
      </>
    );
  };

  const headlineHeroPriorityTemplate = () => {
    return (
      <>
        <div styleName="story-content-inner-wrapper">
          <HeaderCard />
        </div>
        <div styleName="hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="story-content-inner-wrapper">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <AsideCollectionCard />
      </>
    );
  };

  const heroVerticalTemplate = () => {
    return (
      <>
        <HeaderCard />
        <div styleName="hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[3, 4]]} defaultFallback={false} isStoryPageImage />
        </div>
        <CaptionAttribution story={story} config={config} />
        <div styleName="story-content-inner-wrapper">
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <AsideCollectionCard />
      </>
    );
  };

  const heroPriorityLeftTemplate = () => {
    return (
      <>
        <div styleName="hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="story-content-inner-wrapper">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <SideColumn />
      </>
    );
  };

  const headlinePriorityTemplate = () => {
    return (
      <>
        <HeaderCard />
        <div styleName="story-content-inner-wrapper hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="story-content-inner-wrapper">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <SideColumn />
      </>
    );
  };

  const headlineOverlayTemplate = () => {
    return (
      <>
        <div styleName="hero-image index-2">
          <HeroImage story={story} FullBleed={false} aspectRatio={[[3, 4], [16, 9]]} isStoryPageImage />
        </div>
        <HeaderCard />
        <div styleName="story-content-inner-wrapper">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        <AsideCollectionCard />
      </>
    );
  };

  const getStoryTemplate = templateType => {
    switch (templateType) {
      case "hero-priority-center":
        return heroPriorityCenterTemplate();
      case "default":
        return heroPriorityLeftTemplate();
      case "headline-hero-priority":
        return headlineHeroPriorityTemplate();
      case "hero-vertical-priority":
        return heroVerticalTemplate();
      case "headline-priority":
        return headlinePriorityTemplate();
      case "headline-overlay-priority":
        return headlineOverlayTemplate();
    }
  };

  return <>{getStoryTemplate(templateType)}</>;
};

StoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.shape({
    templateType: PropTypes.string,
    authorDetails: PropTypes.object,
    asideCollection: PropTypes.object
  }),
  children: PropTypes.node,
  storyElementsConfig: PropTypes.object,
  widgetComp: PropTypes.func,
  adComponent: PropTypes.func
};
